import { DashboardContent } from "src/layouts/dashboard";
import { Card, Dialog, Stack } from "@mui/material";
import { EmptyContent } from "src/components/empty-content";
import { PermissionGuard } from "src/routes/components";
import {Datagrid, List, useTranslate, EditButton, DeleteButton, FunctionField, TextField, CreateButton, Button } from "react-admin";
import { XButton } from "src/pages/admin/components/XButton";
import { Iconify } from "src/components/iconify";
import AuditLogDialog from "src/pages/admin/components/AuditLog/AuditLogDialog";
import { Resources } from "../../resource";
import { useState, ReactElement } from "react";
import EditPopup from "./Edit";
import { CustomBreadcrumbs } from "src/components/custom-breadcrumbs";
import CreatePopup from "./Create";
import { useSyncNats } from 'src/hooks/use-sync-nats';

const View = () => {
    const translate = useTranslate();
    const [popup, setPopup] = useState<ReactElement>();
    const [auditLogOpen, setAuditLogOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [auditTitle, setAuditTitle] = useState('Audit Logs');
    const [syncing, setSyncing] = useState(false);
    const { handleSync } = useSyncNats();
    
    const handleSyncNats = async () => {
        setSyncing(true);
        try {
            await handleSync(Resources.predictionConfigs);
        } finally {
            setSyncing(false);
        }
    };
    

  return (
    <DashboardContent>
       <CustomBreadcrumbs
        heading={translate('module.prediction.system-configs.title')}
        action={
            <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
            >
            <PermissionGuard resource="prediction-system-configs" action="create" fallback={<></>}>
                <CreateButton
                size="medium"
                variant="contained"
                onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreatePopup onClose={() => setPopup(undefined)} />);
                }}
                />
            </PermissionGuard>
            <PermissionGuard resource="prediction-system-configs" action="update" fallback={<></>}>
                <Button
                variant="contained"
                size="medium"
                color="info"
                disabled={syncing}
                onClick={handleSyncNats}
                label={translate('module.natsSync.sync')}
                >
                <Iconify icon="mdi:cloud-sync" />
                </Button>
            </PermissionGuard>
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('module.prediction.system-configs.title')}`);
                    setAuditLogOpen(true);
                }}
                label={translate('resource.auditLogs')}
                >
                <Iconify icon="ph:note" />
                </Button>
            </PermissionGuard>
            </Stack>
        }
        />
        <List exporter={false} actions={false} empty={false} pagination={false}>
          <Card>
          <Datagrid bulkActionButtons={false} rowClick="expand" empty={<EmptyContent />}>
            
            <TextField
              source="key"
              label="module.prediction.system-configs.key"
              sx={{
                whiteSpace: { xs: 'normal', sm: 'nowrap', md: 'nowrap' },
                wordBreak: 'break-all',
              }}
            />
            <TextField
              source="value"
              label="module.prediction.system-configs.value"
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-all',
                display: { xs: 'none', sm: 'block', md: 'block' },
              }}
            />
            <FunctionField
              textAlign="left"
              render={(record: any) => (
                <Stack direction="row" spacing={4} sx={{ justifyContent: 'flex-end' }}>
                  <PermissionGuard resource="prediction-system-configs" action="update" fallback={<></>}>
                    <EditButton
                      onClick={(e) => {
                        e.preventDefault();
                        setPopup(<EditPopup id={record.id} onClose={() => setPopup(undefined)} />);
                      }}
                    />
                  </PermissionGuard>
                  <PermissionGuard resource="prediction-system-configs" action="delete" fallback={<></>}>
                    <DeleteButton mutationMode="pessimistic" />
                  </PermissionGuard>
                  <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                    <XButton
                      variant="text"
                      size="medium"
                      color="secondary"
                      disabled={!record.hasAuditLog}
                      onClick={() => {
                        setSelectedRecord(record);
                        setAuditTitle(`${translate('resource.auditLogs')} - (#${record.id})`);
                        setAuditLogOpen(true);
                      }}
                      label={translate('resource.auditLogs')}
                    >
                      <Iconify icon="ph:note" />
                    </XButton>
                  </PermissionGuard>
                </Stack>
              )}
            />
            </Datagrid>
          </Card>
        </List>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
            {popup}
        </Dialog>
        <AuditLogDialog
            open={auditLogOpen}
            onClose={() => setAuditLogOpen(false)}
            resource={Resources.predictionConfigs}
            recordId={selectedRecord?.id}
            title={auditTitle}
        />
    </DashboardContent>
  )
};

export default View;